import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import useUnitProvider from '/@/core/context/unit';
import { getCornPlantingDensity } from '/@/core/density';
import { convertAreaFromM2 } from '/@/hooks/geometry/area';
import { useCornDensityCurveForProducts, useFertilizationCoefficientsForProducts } from '/@/hooks/recommendation';
import { getCropConfig } from '/@/store/private-data/recommendations/selectors';
import { getActiveRecommendation } from '/@/store/recommendations/selectors';
import type { Field } from '/@/types/field';
import type { DensityTableValue } from '../hybrids/hybrid-card';
import { useHybrids } from '../hybrids/use-hybrids';

/** Plants per hectare per unit of the API's density scale. */
const DENSITY_ANCHOR = 10000;
/** Share of the total bags planted with the Bt hybrid; the remainder is the refuge. */
export const BT_SHARE = 0.9;
/** The curve is requested over this density window, in the API's own scale. */
const CURVE_RANGE = { minValue: 3, maxValue: 12, step: 0.05 };
/** Fallback when the user hasn't set an achievement percentage. */
export const DEFAULT_PLANTER_EFFICIENCY = 90;

/** Line colours, in selection order — shared by the chart and the card dots. */
export const HYBRID_COLORS = ['#54344B', '#00B0F0', '#8CC63E', '#E1A900'];
/** Used when a hybrid's index falls outside the palette; the first palette entry. */
export const FALLBACK_HYBRID_COLOR = '#54344B';

/** Shape of the yield/density curve endpoint, keyed by product id. `useSWR` types it as `{}`. */
type CurvePoint = { density: number; yield: number };
type CurveByProduct = Record<number, { recommended?: CurvePoint; points?: CurvePoint[] } | undefined>;
type NitrogenValue = number | { common?: number; recommended?: number };
/** The endpoint returns the full nutrient set; the step only surfaces N, P, K and S. */
type FertilizationByProduct = Record<number, { n?: NitrogenValue; p?: number; k?: number; s?: number } | undefined>;

/**
 * The endpoint returns nitrogen as `{ common, recommended }` — `common` from the generic
 * coefficient, `recommended` the hybrid-specific one, which is null when that hybrid has no
 * coefficient for this environment band. Callers need to know which one they got: only the
 * hybrid-specific figure is presented as a recommendation.
 */
const nitrogenFrom = (value?: NitrogenValue) => {
    if (value === undefined || value === null) return { value: undefined, isRecommended: false };
    if (typeof value === 'number') return { value, isRecommended: false };
    if (typeof value.recommended === 'number') return { value: value.recommended, isRecommended: true };
    return { value: value.common, isRecommended: false };
};

/**
 * Seed and bag figures for one hybrid.
 *
 * Shared with the PDF adapter, which derives the same card from the saved blob rather than
 * from live queries — keeping the arithmetic in one place is what stops the page and the PDF
 * quoting different bag counts.
 */
export const deriveBagFigures = ({
    harvestDensity,
    planterEfficiency,
    seedsPerBag,
    areaInHectares,
}: {
    harvestDensity?: number;
    planterEfficiency: number;
    seedsPerBag?: number;
    areaInHectares?: number;
}) => {
    const { density: plantingDensity, bagsPerHectare } = getCornPlantingDensity(
        harvestDensity,
        planterEfficiency,
        seedsPerBag
    );
    const totalBags = bagsPerHectare && areaInHectares ? Math.round(bagsPerHectare * areaInHectares) : undefined;
    const hybridBags = totalBags !== undefined ? Math.round(BT_SHARE * totalBags) : undefined;

    return {
        plantingDensity,
        bagsPerHectare,
        totalBags,
        hybridBags,
        refugeBags: totalBags !== undefined && hybridBags !== undefined ? totalBags - hybridBags : undefined,
    };
};

export type ManagementHybrid = {
    id: number;
    catalogName: string;
    color: string;
    /** Plants per hectare at harvest. */
    harvestDensity?: number;
    estimatedYield?: number;
    /** kg/ha of nitrogen — the hybrid-specific figure when there is one, else the generic. */
    nitrogen?: number;
    /** Whether `nitrogen` is the hybrid-specific figure rather than the generic fallback. */
    nitrogenIsRecommended: boolean;
    /** kg/ha of phosphorus, potassium and sulfur for the estimated yield. */
    phosphorus?: number;
    potassium?: number;
    sulfur?: number;
    /** Seeds per hectare to sow to reach the harvest density. */
    plantingDensity?: number;
    bagsPerHectare?: number;
    totalBags?: number;
    hybridBags?: number;
    refugeBags?: number;
    /** `{density (pl/ha), yield}` points for the curve. */
    points: { density: number; yield: number }[];
    /** Recommended density per environment range, for products with a data-backed table. */
    densityTableValues: DensityTableValue[];
};

/**
 * Everything the "Manejo recomendado" step shows, per selected hybrid.
 *
 * Density comes from the yield/density curve, nitrogen from the fertilization coefficients
 * for that hybrid's recommended yield, and the seed/bag figures from the same helpers the
 * legacy planting-density calculator uses, so the two agree.
 */
export const useManagementData = ({ field, crop = 'corn' }: { field?: Field; crop?: 'corn' | 'soy' }) => {
    const recommendation = useSelector(getActiveRecommendation);
    const cropConfig = getCropConfig(recommendation, crop);
    const units = useUnitProvider();

    const productIds: number[] = cropConfig?.products || [];
    const environment: number | undefined = cropConfig?.environment;
    const planterEfficiency: number = cropConfig?.userPlanterEfficiency || DEFAULT_PLANTER_EFFICIENCY;

    // The step only ever shows hybrids picked on the previous step, but they can come from
    // either tab, so look through both lists.
    const { featuredProducts, otherProducts } = useHybrids({ field, crop });
    const catalog = useMemo(() => [...featuredProducts, ...otherProducts], [featuredProducts, otherProducts]);

    const curveQuery = useCornDensityCurveForProducts({
        productsId: productIds,
        environment,
        ...CURVE_RANGE,
        crop,
    });

    const curves = curveQuery.data as CurveByProduct | undefined;

    // The endpoint drops products with no estimated yield, so pass only the resolved ones.
    const fertilizationProducts = useMemo(
        () =>
            productIds
                .map((id) => ({ id, estimatedYield: curves?.[id]?.recommended?.yield }))
                .filter((product): product is { id: number; estimatedYield: number } => !!product.estimatedYield),
        [productIds, curves]
    );
    const fertilizationQuery = useFertilizationCoefficientsForProducts({
        environment: environment as number,
        products: fertilizationProducts,
    });
    const fertilization = fertilizationQuery.data as FertilizationByProduct | undefined;

    /** Hectares used for the total-bag figures: the user's override, else the field's area. */
    const areaInHectares: number | undefined = useMemo(() => {
        if (cropConfig?.userArea) return Number(cropConfig.userArea);
        if (!field?.area) return undefined;
        return convertAreaFromM2(field.area, units).value;
    }, [cropConfig?.userArea, field?.area, units]);

    const hybrids: ManagementHybrid[] = useMemo(() => {
        return productIds.map((id, index) => {
            const product = catalog.find((candidate) => candidate.id === id);
            const curve = curves?.[id];

            const harvestDensity = curve?.recommended?.density ? curve.recommended.density * DENSITY_ANCHOR : undefined;
            const bags = deriveBagFigures({
                harvestDensity,
                planterEfficiency,
                seedsPerBag: product?.seedsPerBag,
                areaInHectares,
            });
            const nitrogen = nitrogenFrom(fertilization?.[id]?.n);

            return {
                id,
                catalogName: product?.catalogName || '',
                color: HYBRID_COLORS[index % HYBRID_COLORS.length] ?? FALLBACK_HYBRID_COLOR,
                harvestDensity,
                estimatedYield: curve?.recommended?.yield,
                nitrogen: nitrogen.value,
                nitrogenIsRecommended: nitrogen.isRecommended,
                phosphorus: fertilization?.[id]?.p,
                potassium: fertilization?.[id]?.k,
                sulfur: fertilization?.[id]?.s,
                ...bags,
                points: (curve?.points || []).map((point) => ({
                    density: point.density * DENSITY_ANCHOR,
                    yield: point.yield,
                })),
                densityTableValues: product?.densityTableValues || [],
            };
        });
    }, [productIds, catalog, curves, fertilization, planterEfficiency, areaInHectares]);

    return {
        hybrids,
        planterEfficiency,
        areaInHectares,
        isLoading: curveQuery.isLoading || (curveQuery.isValidating && !curveQuery.data),
        // The fertilization figures resolve a request later than the curve they depend on.
        isLoadingFertilization:
            curveQuery.isLoading ||
            fertilizationQuery.isLoading ||
            (fertilizationQuery.isValidating && !fertilizationQuery.data),
    };
};
